/**
 * External Imports
 */
import { FC, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuButton, NeuImage } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import { BlueChip } from '../../shared/chips/blueChip';
import { Dropdown } from '../../shared/dropdowns';
import IssueSubHeader from '../reusableModalPieces/subHeader';
import SearchView from '../reusableModalPieces/searchView';

import { RootState } from '../../../redux/store';
import {
  getRound,
  clearStoplight,
  getStoplightById,
  addTaskToSharingQ,
  clearTaskModalState,
  clearTicketDetails,
  getDetailsByTicketNum,
  postNewTask,
  setToastType
} from '../../../redux/actions';
import {
  useLazyGetEmployeesByIdsQuery,
  useLazyGetEmployeesBySearchQuery
} from '../../../redux/apis/Employees.api';

import { handleModal } from '../../../services/ModalPortal/utils';
import useToast from '../../../services/Toast';

import { logTaskClick } from '../../../utils/analyticsHelpers';
import { useDebounceValue } from '../../../utils/debouncers';
import {
  abbreviatedName,
  compareObjByStringKey,
  date,
  formatPhoneNumber,
  toTitleCase
} from '../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Employee,
  Issue,
  Stoplight,
  TaskPost,
  Unit
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CardHeader,
  CardMainHeader,
  CardTitle,
  FooterDiv,
  FooterText,
  IssueViewDiv,
  IssueViewDiv2Column,
  IssueViewSubDiv,
  LabelContentPatient,
  LabelContentBold,
  LabelHeading,
  ModalCardContent,
  ModalFooter,
  TicketNumber,
  TitleSpanView,
  ViewIssueCard,
  ViewStoplightsClickableLabel
} from './AddIssueModal.styles';
import { ChipWrapper } from '../feedbackModal/FeedbackModal.styles';

/**
 * Static File Imports
 */
import serviceCentralLogoImg from '../../../assets/images/service_central_icon.png';

/**
 * Constant Imports
 */
import serviceCentralCategories from '../../../constants/serviceCentralCategories.json';

interface IIssueModalViewProps {
  authorizedUser: AuthUserProps;
  issueData: TaskPost;
  stoplight?: Stoplight | null;
  tempModalState: { redirect: boolean };
  ticketDetails: string;
  units: Unit[];
}

const IssueModalView: FC<IIssueModalViewProps> = ({
  authorizedUser,
  issueData,
  stoplight,
  tempModalState,
  ticketDetails,
  units
}) => {
  const [taskObject, setTaskObject] = useState<TaskPost>(issueData);
  const [shareObject, setShareObject] = useState<{
    taskId: string;
    userName: string;
    email: string[];
  }>({ taskId: '', userName: '', email: [] });
  const [activeSearch, setActiveSearch] = useState<string>('');
  const [sharedSearchTerm, setSharedSearchTerm] = useState('');
  const [sharedPopState, setSharedPopState] = useState<boolean>(false);
  const [view, setView] = useState<'info' | 'search'>('info');

  const modalContentRef = useRef<HTMLNeuCardElement>(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const { toast } = useToast();

  const { logTrackingEvent } = useAnalyticsApi();

  const dbSharedSearchTerm = useDebounceValue(sharedSearchTerm, 250);

  const deepLinkTaskView = pathname.includes('task/complete');

  const [
    getEmployeesByIds,
    {
      data: sharedEmployeesData
      // isError: isEmployeesByIdsError,
      // isFetching: isEmployeesByIdsFetching,
      // isLoading: isEmployeesByIdsLoading
    }
  ] = useLazyGetEmployeesByIdsQuery();
  const [
    getEmployeesBySearch,
    {
      data: employeesBySearchData
      // isError: isGetEmployeesBySearchError,
      // isFetching: isGetEmployeesBySearchFetching,
      // isLoading: isGetEmployeesBySearchLoading
    }
  ] = useLazyGetEmployeesBySearchQuery();

  const handleClose = () => {
    logTrackingEvent(logTaskClick('issues', 'close'));
    dispatch(clearTaskModalState());
    dispatch(clearStoplight());
    dispatch(clearTicketDetails());
    handleModal(modalContentRef);
    if (deepLinkTaskView) {
      navigate(
        taskObject.roundingType === 'employee'
          ? '/employees/issues'
          : '/patients/issues'
      );
    }
  };

  const handleRemoveSharedWith = (emp: string) => {
    let removedEmpName: string;
    const spaces = emp.split(' ').length - 1;
    if (spaces === 2) {
      removedEmpName = emp.split(' ').slice(0, 2).join('.');
    } else {
      const splittedArray = emp.split(' ');
      removedEmpName = `${splittedArray[0] + splittedArray[1]}.${
        splittedArray[2]
      }`;
    }
    const removedEmpId = emp.split(/[\s+]+/).pop() || '';
    // Update sharedWith in task object
    const newSharedWith = taskObject.sharedWith.filter((item: string) => {
      return item.includes(removedEmpId?.toLowerCase()) === false;
    });
    setTaskObject({
      ...taskObject,
      sharedWith: newSharedWith
    });

    // update Emails for sharing
    const emailsAfterRemove = shareObject.email.filter((email: string) => {
      const existingString = email.split('@')[0];
      return existingString !== removedEmpName;
    });
    setShareObject({ ...shareObject, email: emailsAfterRemove });
  };

  const handlePost = () => {
    const payload = {
      ...taskObject,
      updated: date.now(),
      stoplightId: stoplight?.id || ''
    };
    delete payload.discussion;
    if (shareObject.userName) {
      dispatch(addTaskToSharingQ(shareObject));
    }
    dispatch(postNewTask(payload));
    dispatch(clearStoplight());
    dispatch(clearTaskModalState());
    handleModal(modalContentRef);
    if (deepLinkTaskView) {
      navigate(
        taskObject.roundingType === 'employee'
          ? '/employees/issues'
          : '/patients/issues'
      );
    }
  };

  const handleRedirect = () => {
    if (taskObject?.roundId) {
      if (taskObject?.roundingType === 'patient') {
        if (taskObject?.facilityId && taskObject?.accountNum) {
          handleModal(modalContentRef);
          const patientMeta = {
            facilityId: taskObject.facilityId,
            accountNum: taskObject.accountNum,
            unitId: taskObject.unitId
          };
          if (taskObject?.accountNum?.substring(0, 2) === 'UL') {
            navigate('/patients/profile', {
              state: {
                patientMeta: {
                  ...patientMeta,
                  firstName: taskObject.patientFirstName,
                  lastName: taskObject.patientLastName,
                  mrn: taskObject.patientMRN,
                  room: taskObject.room,
                  bed: taskObject.bed,
                  lastRoundId: taskObject.roundId
                },
                isUnlistedPatient: true
              }
            });
          } else {
            navigate('/patients/profile', {
              state: {
                patientMeta,
                isUnlistedPatient: false
              }
            });
          }
        }
      } else {
        handleModal(modalContentRef);
        navigate('/employees/profile', {
          state: {
            from: pathname,
            employeeId: taskObject?.employeeId,
            mode: undefined
          }
        });
      }
    }
  };

  const handleRemoveAttachStoplight = (
    e: MouseEvent<HTMLNeuIconElement>,
    sl: Stoplight
  ) => {
    e.stopPropagation();
    dispatch(setToastType('Delete Stoplight'));
    if (sl.id === taskObject.stoplightId) {
      toast(
        `Are you sure you would like to un-attach stoplight ${sl.friendlyId}?`,
        'info',
        null,
        400,
        true,
        null,
        () => {
          dispatch(clearStoplight());
        }
      );
    } else {
      dispatch(clearStoplight());
    }
  };

  /* Code block for navigating to round from issue modal */
  const handleSharedSearching = (e: CustomEvent<any>) => {
    const target = e.target as HTMLInputElement;
    setSharedSearchTerm(target.value);
  };

  const openTicketDetailsTab = () => {
    if (ticketDetails) {
      window.open(ticketDetails, '_blank');
    }
  };

  const setEmployeeToShare = (emp: Employee) => {
    const empId = emp.employeeId;
    const empExists = taskObject.sharedWith.includes(empId);
    // if the employee already exists in share, do not add again
    if (!empExists) {
      setTaskObject({
        ...taskObject,
        sharedWith: [...taskObject.sharedWith, empId]
      });
      setShareObject({
        taskId: taskObject.taskId || '',
        userName: `${authorizedUser.firstName} ${authorizedUser.lastName}`,
        email: [...shareObject.email, emp.email]
      });
    } else {
      // TODO future development add a toast here saying 'user already selected'
      console.log('User already selected');
    }
  };

  // adding stoplight to issue
  const setStoplightToAttach = (sl: Stoplight) => {
    if (sl.id) {
      dispatch(getStoplightById(sl.id));
    }
  };

  const isAnimated = useMemo(
    () => (!tempModalState?.redirect ? 'show-modal' : ''),
    [tempModalState]
  );

  const unitName = useMemo(() => {
    const foundUnit: Unit[] = units.filter(
      (unit: Unit) => unit.unitId === issueData.unitId
    );
    return foundUnit[0]?.unit;
  }, [issueData, units]);

  const sharedWithDisplay = useMemo(
    () =>
      sharedEmployeesData?.map((emp: Employee) => {
        const empId = emp.employeeId?.toUpperCase();
        return `${emp.firstName} ${emp.lastName} ${empId}`;
      }) ?? [],
    [sharedEmployeesData]
  );

  // shared with search
  useEffect(() => {
    getEmployeesBySearch(dbSharedSearchTerm);
  }, [dbSharedSearchTerm]);

  useEffect(() => {
    if (taskObject?.stoplightId) {
      dispatch(getStoplightById(taskObject.stoplightId));
    }
  }, [taskObject.stoplightId]);

  useEffect(() => {
    if (taskObject?.ticketNumber) {
      dispatch(getDetailsByTicketNum(taskObject.ticketNumber));
    }
  }, [taskObject.ticketNumber]);

  useEffect(() => {
    if (employeesBySearchData?.length > 0 && dbSharedSearchTerm.length > 0) {
      setSharedPopState(true);
    }
  }, [employeesBySearchData]);

  /* Code block for navigating to round from issue modal */
  useEffect(() => {
    if (taskObject?.roundId) {
      dispatch(
        getRound({
          roundId: taskObject.roundId,
          fromHistoryDrawer: false,
          type: taskObject.roundingType,
          ...(taskObject.roundingType === 'patient'
            ? { accountNum: taskObject.accountNum, unitId: taskObject.unitId }
            : {})
        })
      );
    }
    if (taskObject?.sharedWith && taskObject.sharedWith.length > 0) {
      getEmployeesByIds([...taskObject.sharedWith]);
    }
  }, [taskObject]);

  return (
    <ViewIssueCard
      id="Modal-View-Issues"
      className={isAnimated}
      unit="card"
      ref={modalContentRef}
    >
      <CardHeader className="neu-bg-plain-0" unit="header">
        <CardMainHeader className="d-flex flex-column">
          <CardTitle>
            <TitleSpanView>
              {taskObject.roundingType &&
                `${toTitleCase(taskObject.roundingType)} | ${
                  serviceCentralCategories.includes(
                    taskObject.category as string
                  ) ||
                  (taskObject.taskSubtype &&
                    serviceCentralCategories.includes(taskObject.taskSubtype))
                    ? 'Service Central Issue'
                    : 'Issue'
                }`}
            </TitleSpanView>
          </CardTitle>
          <NeuButton
            aria-label="close panel"
            data-testid="view-sc-issue-modal-close-button"
            kind="tertiary"
            color="blue"
            icon="close"
            size="small"
            onClick={handleClose}
          >
            Close
          </NeuButton>
        </CardMainHeader>
        <IssueSubHeader
          completed={taskObject.completed}
          items={stoplight?.id}
          type="issue"
          view={view}
          setView={setView}
        />
      </CardHeader>
      {view === 'info' ? (
        <ModalCardContent unit="content">
          <LabelHeading>Issue ID</LabelHeading>
          <LabelContentBold>
            {taskObject?.friendlyId ? taskObject.friendlyId : 'Unknown'}
          </LabelContentBold>
          <IssueViewDiv2Column>
            {taskObject.roundingType === 'patient' ? (
              <IssueViewSubDiv>
                <LabelHeading>Patient</LabelHeading>
                <LabelContentPatient
                  color={
                    taskObject &&
                    taskObject.facilityId &&
                    taskObject.accountNum &&
                    taskObject.roundId
                      ? '#206DA3'
                      : '#191919'
                  }
                  cursorType={
                    !!(
                      taskObject &&
                      taskObject.facilityId &&
                      taskObject.accountNum &&
                      taskObject.roundId
                    )
                  }
                  onClick={
                    taskObject &&
                    taskObject.facilityId &&
                    taskObject.accountNum &&
                    taskObject.roundId
                      ? () => handleRedirect()
                      : undefined
                  }
                >
                  {`${
                    !taskObject.patientLastName || !taskObject.patientFirstName
                      ? 'Unknown'
                      : abbreviatedName(
                          taskObject.patientFirstName,
                          taskObject.patientLastName
                        )
                  }`}
                </LabelContentPatient>
              </IssueViewSubDiv>
            ) : (
              <div>
                <LabelHeading>Employee</LabelHeading>
                <LabelContentPatient
                  color={
                    taskObject && taskObject.employeeId && taskObject?.roundId
                      ? '#206DA3'
                      : '#191919'
                  }
                  cursorType={
                    !!(
                      taskObject &&
                      taskObject.employeeId &&
                      taskObject?.roundId
                    )
                  }
                  onClick={
                    taskObject && taskObject.employeeId && taskObject?.roundId
                      ? () => handleRedirect()
                      : undefined
                  }
                >
                  {`${
                    taskObject.employeeLastName
                      ? `${toTitleCase(taskObject.employeeLastName)}, `
                      : ''
                  }${
                    taskObject.employeeFirstName
                      ? toTitleCase(taskObject.employeeFirstName)
                      : ''
                  }`}
                  {`${
                    !taskObject.employeeLastName &&
                    !taskObject.employeeFirstName
                      ? 'Unknown'
                      : ''
                  }`}
                  {`${
                    taskObject.employeeId
                      ? ` | ${taskObject.employeeId.toUpperCase()}`
                      : ''
                  }`}
                </LabelContentPatient>
              </div>
            )}

            {taskObject.roundingType === 'patient' && (
              <IssueViewSubDiv>
                <LabelHeading>Room</LabelHeading>
                <LabelContentBold>
                  {taskObject.room
                    ? `${taskObject.room}${taskObject.bed || ''}`
                    : 'N/A'}
                </LabelContentBold>
              </IssueViewSubDiv>
            )}
          </IssueViewDiv2Column>
          <IssueViewDiv2Column>
            <IssueViewSubDiv>
              <LabelHeading>Creator</LabelHeading>
              <LabelContentBold>
                {taskObject.userFullName || 'Unknown'}
              </LabelContentBold>
            </IssueViewSubDiv>
          </IssueViewDiv2Column>
          {(serviceCentralCategories.includes(taskObject.category as string) ||
            (taskObject.taskSubtype &&
              serviceCentralCategories.includes(taskObject.taskSubtype))) && (
            <IssueViewDiv2Column>
              <IssueViewSubDiv>
                <LabelHeading>Ticket Number</LabelHeading>
                <LabelContentBold>
                  <TicketNumber onClick={openTicketDetailsTab}>
                    {taskObject.ticketNumber}
                  </TicketNumber>
                </LabelContentBold>
              </IssueViewSubDiv>
              <IssueViewSubDiv>
                <LabelHeading>Callback Number</LabelHeading>
                <LabelContentBold>
                  {taskObject && taskObject.callback
                    ? formatPhoneNumber(taskObject.callback)
                    : taskObject.u_best_contact_info
                    ? formatPhoneNumber(taskObject?.u_best_contact_info)
                    : 'Unknown'}
                </LabelContentBold>
              </IssueViewSubDiv>
            </IssueViewDiv2Column>
          )}
          {/* TODO: should cat and subcat only show with service central */}
          <IssueViewDiv>
            <LabelHeading>Category</LabelHeading>
            <LabelContentBold className="d-flex align-items-center">
              {(serviceCentralCategories.includes(
                taskObject.category as string
              ) ||
                (taskObject.taskSubtype &&
                  serviceCentralCategories.includes(
                    taskObject.taskSubtype
                  ))) && (
                <NeuImage
                  width="25"
                  alt="service central logo"
                  src={serviceCentralLogoImg}
                />
              )}
              {taskObject.category || taskObject.taskSubtype || 'Unknown'}
            </LabelContentBold>
          </IssueViewDiv>
          {taskObject.subCategory && (
            <IssueViewDiv>
              <LabelHeading>Subcategory</LabelHeading>
              <LabelContentBold>
                {taskObject.subCategory || 'Unknown'}
              </LabelContentBold>
            </IssueViewDiv>
          )}
          {taskObject.u_non_routine_justification && (
            <IssueViewDiv>
              <LabelHeading>Non-routine Item Justification</LabelHeading>
              <LabelContentBold>
                {taskObject.u_non_routine_justification}
              </LabelContentBold>
            </IssueViewDiv>
          )}
          {/* *********************************************************** */}
          {taskObject.roundingType === 'patient' ? (
            <IssueViewDiv>
              <LabelHeading>Unit</LabelHeading>
              <LabelContentBold>{`${
                taskObject.unitId ? unitName : 'Unknown'
              }`}</LabelContentBold>
            </IssueViewDiv>
          ) : (
            <IssueViewDiv>
              <LabelHeading>Department</LabelHeading>
              <LabelContentBold>
                {taskObject.department || 'Unknown'}
              </LabelContentBold>
            </IssueViewDiv>
          )}
          <IssueViewDiv>
            <LabelHeading>Details</LabelHeading>
            <LabelContentBold>
              {taskObject.description || 'Unknown'}
            </LabelContentBold>
          </IssueViewDiv>
          <IssueViewDiv>
            <LabelHeading>Share With</LabelHeading>
            <div onBlur={() => setSharedPopState(false)}>
              <div className="pt-1 pb-2">
                {(sharedWithDisplay &&
                  sharedWithDisplay.length > 0 &&
                  sharedWithDisplay.map((emp: string) => {
                    return (
                      <ChipWrapper
                        key={emp}
                        onClick={() => handleRemoveSharedWith(emp)}
                      >
                        <BlueChip key={emp} name={emp} />
                      </ChipWrapper>
                    );
                  })) ||
                  []}
              </div>
              <div className="position-relative">
                <Dropdown
                  id="shared-with-dropdown"
                  name="sharedWith"
                  classes="ps-0 pe-1"
                  open={sharedPopState}
                  focused={activeSearch === 'sharedWith'}
                  placeholder="Start typing name or 3-4 ID"
                  query={sharedSearchTerm}
                  setOpen={setSharedPopState}
                  typeAhead
                  setTypeAhead={setActiveSearch}
                  setTypeAheadChange={handleSharedSearching}
                >
                  {[...(employeesBySearchData ?? ([] as Employee[]))]
                    .sort(compareObjByStringKey('lastName'))
                    .sort(compareObjByStringKey('firstName'))
                    .map((emp: Employee) => (
                      <div key={emp.employeeId}>
                        <div
                          className="dropdown-hover-item"
                          aria-selected={taskObject.sharedWith.includes(
                            emp.employeeId
                          )}
                          role="option"
                          tabIndex={0}
                          onMouseDown={() => {
                            setEmployeeToShare(emp);
                            setActiveSearch('');
                            setSharedSearchTerm('');
                          }}
                          onKeyDown={() => {
                            setEmployeeToShare(emp);
                            setActiveSearch('');
                            setSharedSearchTerm('');
                          }}
                        >
                          <p>
                            {emp.firstName.toUpperCase()}{' '}
                            {emp.lastName.toUpperCase()}
                          </p>
                          <p style={{ fontSize: '14px' }}>{emp.employeeId}</p>
                          <p style={{ fontSize: '14px' }}>{emp.title}</p>
                        </div>
                        {[...(employeesBySearchData ?? ([] as Employee[]))]
                          .length > 1 && <hr />}
                      </div>
                    ))}
                </Dropdown>
              </div>
            </div>
          </IssueViewDiv>
          <IssueViewDiv>
            <ViewStoplightsClickableLabel
              className="mt-4 mb-3"
              onClick={() => setView('search')}
            >
              {taskObject.stoplightId ||
              (taskObject.completed && !taskObject.stoplightId)
                ? 'View Attached Stoplight'
                : 'Attach to Stoplight'}
            </ViewStoplightsClickableLabel>
          </IssueViewDiv>
        </ModalCardContent>
      ) : (
        <ModalCardContent
          id="Issues-Modal-Stoplight-Card"
          className="pb-4"
          unit="content"
        >
          <SearchView
            attachedDisplay={
              stoplight && Object.keys(stoplight).length > 0
                ? ([stoplight] as (Issue & Stoplight)[])
                : ([] as (Issue & Stoplight)[])
            }
            attachToItem={taskObject as TaskPost & Stoplight}
            attachToItemCompleted={taskObject.completed}
            prevAttached={stoplight?.id ? [stoplight.id] : []}
            type="stoplight"
            handleRemoveAttached={handleRemoveAttachStoplight}
            setItemsToAttach={setStoplightToAttach}
          />
        </ModalCardContent>
      )}
      <ModalFooter className="px-1" unit="footer">
        <FooterDiv>
          {(serviceCentralCategories.includes(taskObject.category as string) ||
            (taskObject.taskSubtype &&
              serviceCentralCategories.includes(taskObject.taskSubtype))) && (
            <>
              <FooterText className="pe-1">
                {taskObject.completed
                  ? 'This issue was marked complete in Service \nCentral.'
                  : 'This issue is being worked in Service Central.'}
              </FooterText>
              <NeuButton
                data-testid="view-sc-issue-modal-save-button"
                size="small"
                onClick={() => handlePost()}
              >
                Save
              </NeuButton>
            </>
          )}
        </FooterDiv>
      </ModalFooter>
    </ViewIssueCard>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    issueData: state.ModalReducer.modalData,
    modalType: state.ModalReducer.modalType,
    stoplight: state.StoplightReducer.stoplight,
    tempModalState: state.TaskReducer.tempModalState,
    ticketDetails: state.TaskReducer.ticketDetails,
    units: state.ConfigReducer.units
  };
};

export default connect(mapReduxStateToProps)(IssueModalView);
